<template>
  <div
    class="bills-to-card-table-item"
    @click="$emit('click', billsRegistry.id)"
  >
    <div class="transaction-table-item">
      <div class="transaction-table-item__short-info">
        <div class="transaction-table-item__col transaction-table-item__date">
          <div class="transaction-table-item__date-main">
            {{ billsRegistry.dateTime.date }}
          </div>
          <div class="transaction-table-item__date-time">
            {{ billsRegistry.dateTime.time }}
          </div>
        </div>
        <div class="transaction-table-item__col transaction-table-item__name">
          {{ billsRegistry.registryName }}
        </div>
        <div
          class="transaction-table-item__col transaction-table-item__p-count"
        >
          {{ billsRegistry.billRegistryData.total_count }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__p-sum">
          {{ billsRegistry.billRegistryData.total_sum }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__status">
          <transaction-status
            :status-id="billsRegistry.status"
          ></transaction-status>
        </div>
        <transactions-table-actions-button
          :actions="itemActions"
          ref="transactionActionsMenu"
          @actionFired="handleActionFired"
        >
        </transactions-table-actions-button>
      </div>
    </div>
    <bills-registry-edit-popup ref="editPopup"></bills-registry-edit-popup>
    <success-notification
      ref="editRegistryNotification"
      :text="$t('payouts.by-registry.registry.notifications.updated-success')"
    ></success-notification>
    <success-notification
      ref="billsRegistryProcessingNotification"
      :text="
        $t('payouts.by-registry.created.notifications.registry-in-progress')
      "
    ></success-notification>
    <bills-registry-delete-popup
      ref="billsRegistryDeletePopup"
    ></bills-registry-delete-popup>
  </div>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
import TransactionStatus from '@vue-app/components/transaction/TransactionStatus.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { mapState } from 'vuex'
import Loader from '@vue-app/components/base/Loader.vue'
import BillsRegistry from '@js-app/entities/payouts/BillsRegistry'
import TransactionsTableActionsButton from '@vue-app/components/company/transactions/table/TransactionsTableActionsButton.vue'
import BillsRegistryDeletePopup from '../popup/BillsRegistryDeletePopup.vue'
import BillsRegistryEditPopup from '../popup/BillsRegistryEditPopup.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'

export default {
  name: 'BillsRegistryTableItem',
  components: {
    SuccessNotification,
    TransactionsTableActionsButton,
    Loader,
    TransactionStatus,
    BillsRegistryDeletePopup,
    BillsRegistryEditPopup,
  },
  props: {
    billsRegistry: {
      type: BillsRegistry,
    },
  },
  data: () => ({
    transactionReceiptBlob: null,
  }),
  computed: {
    ...mapState(['selectedCompany']),
    itemActions() {
      let _actions = []

      if (this.billsRegistry.status === 0) {
        _actions.push({
          id: 'delete',
          title: this.$t('bills.by-registry.actions.delete'),
          handler: this.deleteRegistry,
        })
        _actions.push({
          id: 'edit',
          title: this.$t('bills.by-registry.actions.edit-name'),
          handler: this.handleEditRegistry,
        })

        if (this.billsRegistry.billRegistryData.errors_count === 0) {
          _actions.push({
            id: 'process',
            title: this.$t('bills.by-registry.actions.process-registry'),
            handler: this.handleProcessRegistry,
          })
        }
      }

      return _actions
    },
  },
  methods: {
    async handleActionFired(action) {
      try {
        await action.handler()
      } catch (e) {
        console.error(e)
      }

      try {
        this.$refs.transactionActionsMenu.actionFinished(action.id)
      } catch (e) {}
    },
    async handleEditRegistry() {
      let editResponse = await this.$refs.editPopup.open(
        this.billsRegistry.billRegistryData
      )

      if (!editResponse) {
        return
      }

      $emit(this, 'registryItemUpdated', editResponse)
      this.billsRegistry.billRegistryData.name = editResponse.name

      await CompaniesApi.updateBillsRegistry(
        this.selectedCompany,
        editResponse.id,
        {
          registry_name: editResponse.name,
        }
      )

      this.$refs.editRegistryNotification.show()
    },
    async handleProcessRegistry() {
      this.isRegistryProcessing = true
      try {
        this.$refs.billsRegistryProcessingNotification.show()
        this.billsRegistry.billRegistryData.status = 'processing'

        await CompaniesApi.processBillsRegistry(
          this.selectedCompany,
          this.billsRegistry.id
        )
      } finally {
        this.isRegistryProcessing = false
      }
    },
    async deleteRegistry() {
      if (
        !(await this.$refs.billsRegistryDeletePopup.open(
          this.billsRegistry.registryName
        ))
      ) {
        return
      }

      try {
        await CompaniesApi.deleteBillsRegistry(
          parseInt(this.selectedCompany),
          this.billsRegistry.id
        )
        $emit(this, 'deleted', this.billsRegistry)
      } catch (e) {
        console.error(e)
      }
    },
    toPayoutToCardDetails() {
      this.$router.push(
        RouteHelper.getRoute('bill-to-card-view', {
          companyId: this.selectedCompany,
          payoutId: this.transaction.operationId,
        })
      )
    },
  },
  emits: ['click', 'registryItemUpdated', 'deleted'],
}
</script>

<style lang="scss" scoped>
.bills-to-card-table-item {
  .transaction-table-item {
    &__date {
      padding: 0;
    }

    &__short-info {
      box-shadow: 0px 2px 14px transparent;
      transition: box-shadow 0.2s linear;

      &:hover {
        cursor: pointer;
        box-shadow: 0px 2px 14px rgba(208, 217, 231, 0.4);
      }
    }

    &__name {
      word-break: break-all;
      font-weight: 500;
      font-size: 14px;
      line-height: 18px;
      color: #1a1f29;
      opacity: 0.7;
    }

    &__amount {
      font-weight: 600;
      font-size: 14px;
      line-height: 18px;
      color: #1a1f29;
      opacity: 0.7;
      justify-content: flex-end;
    }

    &__p-count {
      justify-content: center;
    }

    &__p-sum {
      justify-content: flex-end;
    }

    &__status {
      align-self: center;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: 20px;
      padding-right: 20px;

      &-item {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 105px;
        max-width: 100%;
        height: 28px;
        border-radius: 6px;
        font-weight: 500;
        font-size: 12px;
        line-height: 28px;

        &--paid {
          background: rgba(39, 204, 36, 0.2);
          color: #44d941;
        }
      }
    }
  }
}
</style>
